// One clinical report on the Clinical reports page: the report itself (a form, saved
// with Save), and — once it exists — the regulatory notifications it calls for and
// its notes, each of which saves on its own as feedback's notes do.
//
// `reportId` is null (closed), "new", or an id. Fetches the record itself, as
// FeedbackDialog does, so the list never holds a copy that could be stale; saving a
// new report keeps the dialog open on it, since notifications and notes can only be
// recorded against a report that exists.
//
// **The affected person's entry and incidents, and the related incident, are shown
// with their clinical content in**, to a reader who may hold no incident permission
// (see backend routers/clinical_report.py). They are read from the *saved* record,
// so they follow a change of person or incident once it's saved. The link runs one
// way: nothing on the incident side says this report exists — so the open button on
// an incident is offered only to a reader who could open it anyway.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, MenuItem, Stack, TextField, Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MedicationIcon from "@mui/icons-material/Medication";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { API_BASE_URL } from "../config";
import {
  CLINICAL_HARM_LEVELS, CLINICAL_REPORT_STATUSES, CLINICAL_REPORT_TYPES,
} from "../constants";
import {
  NOTIFICATION_STATE_COLOR, NOTIFICATION_STATE_TEXT, affectedFromIncident,
  affectedFromParticipant, emptyReportForm, fromLocalInput, reportBody, reportProblems,
  reportToForm, toLocalInput,
} from "../clinicalReportRules";
import { participantMatches } from "../feedbackRules";
import { drugLine, standingDrugs } from "../incidentNotes";
import { useCurrentUser } from "../useCurrentUser";
import { useMe } from "../MeContext";
import { notify } from "../notify";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import DocumentLink from "./DocumentLink";
import HintedSelect from "./HintedSelect";
import PatientIncidentHistory from "./PatientIncidentHistory";
import ParticipantDetailsDialog, {
  ViewParticipantButton, WatchListAlert, participantSummary,
} from "./ParticipantDetails";

const API = `${API_BASE_URL}/clinical-reports`;
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const participantLabel = (p) =>
  [p.name || "(no name)", p.race_number && `#${p.race_number}`, p.race].filter(Boolean).join(" — ");
const incidentLabel = (i) =>
  `#${i.id} ${fmtDateTime(i.created_at)} — ${i.incident_type || "Incident"}: `
  + `${i.incident_summary || "(no summary)"}`
  + (i.patient_name ? ` (${i.patient_name}${i.patient_race_number ? ` #${i.patient_race_number}` : ""})` : "");
const BASIS_TEXT = {
  required: "Required by the type and harm",
  added: "Added by hand",
  former: "No longer required by the type and harm",
};

const Section = ({ title, children, action }) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Typography variant="h6" sx={{ flex: 1 }}>{title}</Typography>
      {action}
    </Box>
    <Stack spacing={1.5}>{children}</Stack>
  </Box>
);

// --- The affected person's entry, drugs and incidents ------------------------

function AffectedPanel({ reportId, savedKey, canOpen }) {
  const [data, setData] = useState(null);
  const [viewEntrant, setViewEntrant] = useState(null);
  useEffect(() => {
    setData(null);
    if (!savedKey) return;
    axios.get(`${API}/${reportId}/affected`).then(res => setData(res.data))
      .catch(() => setData({ participant: null, incidents: [] }));
  }, [reportId, savedKey]);
  if (!savedKey) return null;
  if (!data) return <CircularProgress size={20} />;
  const p = data.participant;
  const drugs = data.incidents.flatMap(i => standingDrugs(i.drug_administrations)
    .map(d => ({ ...d, incident: i })))
    .sort((a, b) => (a.given_at || "").localeCompare(b.given_at || ""));
  return (
    <Stack spacing={1.5}>
      {p && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            <Box component="span" sx={{ color: "text.secondary" }}>On the entry list: </Box>
            {participantSummary(p)}
          </Typography>
          <ViewParticipantButton onClick={() => setViewEntrant(p)} />
        </Box>
      )}
      <WatchListAlert participant={p} showScreening />
      {drugs.length > 0 && (
        <Alert severity="warning" icon={<MedicationIcon />}>
          <Typography variant="subtitle2" gutterBottom>
            {drugs.length} drug{drugs.length === 1 ? "" : "s"} given to them on this event
          </Typography>
          <Stack spacing={0.75}>
            {drugs.map(d => (
              <Box key={d.id}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{drugLine(d)}</Typography>
                {d.notes && (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{d.notes}</Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {fmtDateTime(d.given_at)}{d.given_by ? ` — ${d.given_by}` : ""}
                  {` — incident #${d.incident.id}`}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}
      {data.incidents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No incidents recorded for them at this event.
        </Typography>
      ) : (
        <PatientIncidentHistory incidents={data.incidents} openable={canOpen}
          title={`${data.incidents.length} incident${data.incidents.length === 1 ? "" : "s"} for them at this event`} />
      )}
      <ParticipantDetailsDialog participant={viewEntrant} showScreening
                                onClose={() => setViewEntrant(null)} />
    </Stack>
  );
}

function RelatedIncident({ reportId, incidentId, canOpen }) {
  const [incident, setIncident] = useState(null);
  useEffect(() => {
    setIncident(null);
    if (incidentId == null) return;
    axios.get(`${API}/${reportId}/incident`).then(res => setIncident(res.data)).catch(() => {});
  }, [reportId, incidentId]);
  if (incidentId == null) return null;
  if (!incident) return <CircularProgress size={20} />;
  return (
    <PatientIncidentHistory incidents={[incident]} openable={canOpen}
                            title="The related incident — expand to read it" />
  );
}

// --- Notifications ------------------------------------------------------------

function NotificationCard({ n, canEdit, busy, onPut, onUpload }) {
  const [mode, setMode] = useState(null);   // null | "edit" | "not_needed"
  const [doneAt, setDoneAt] = useState("");
  const [doneBy, setDoneBy] = useState("");
  const [reason, setReason] = useState("");
  const fileRef = useRef(null);

  const startEdit = () => {
    setDoneAt(toLocalInput(n.done_at || new Date().toISOString()));
    setDoneBy(n.done_by || "");
    setMode("edit");
  };
  const startNotNeeded = () => { setReason(n.not_needed_reason || ""); setMode("not_needed"); };
  const done = (p) => p.then(() => setMode(null));

  return (
    <Box sx={{ border: "1px solid", borderColor: n.state === "overdue" ? "error.main" : "divider",
               borderRadius: 1, p: 1.5 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle2">{n.what}</Typography>
          <Typography variant="body2">
            <strong>{n.who}</strong> notifies <strong>{n.to}</strong> — {n.when.charAt(0).toLowerCase() + n.when.slice(1)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {BASIS_TEXT[n.basis] || n.basis}
            {(n.state === "due" || n.state === "overdue") && ` · due by ${fmtDateTime(n.due_at)}`}
          </Typography>
        </Box>
        <Chip size="small" label={NOTIFICATION_STATE_TEXT[n.state] || n.state}
              color={NOTIFICATION_STATE_COLOR[n.state] || "default"} />
      </Box>
      {n.state === "done" && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Done {fmtDateTime(n.done_at)}{n.done_by ? ` by ${n.done_by}` : ""}
        </Typography>
      )}
      {n.state === "not_needed" && (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
          Not needed: {n.not_needed_reason}
        </Typography>
      )}
      {n.document && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Copy: <DocumentLink doc={n.document} urlBase={`${API}/documents`} />
        </Typography>
      )}

      {canEdit && mode === "edit" && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mt: 1.5 }}>
          <TextField size="small" type="datetime-local" label="Done at" value={doneAt}
                     onChange={(e) => setDoneAt(e.target.value)}
                     slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" label="By" value={doneBy} onChange={(e) => setDoneBy(e.target.value)} />
          <Button size="small" variant="contained" disabled={busy || !doneAt || !doneBy.trim()}
                  onClick={() => done(onPut(n.code, { done_at: fromLocalInput(doneAt), done_by: doneBy,
                                                        not_needed_reason: null }))}>
            Save
          </Button>
          <Button size="small" onClick={() => setMode(null)}>Cancel</Button>
        </Box>
      )}
      {canEdit && mode === "not_needed" && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mt: 1.5 }}>
          <TextField size="small" label="Why it isn't needed" value={reason} sx={{ flex: 1, minWidth: 260 }}
                     onChange={(e) => setReason(e.target.value)} multiline />
          <Button size="small" variant="contained" disabled={busy || !reason.trim()}
                  onClick={() => done(onPut(n.code, { not_needed_reason: reason }))}>
            Save
          </Button>
          <Button size="small" onClick={() => setMode(null)}>Cancel</Button>
        </Box>
      )}
      {canEdit && mode == null && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          {n.state !== "done" && (
            <Button size="small" variant="outlined" disabled={busy}
                    onClick={() => onPut(n.code, { stamp: true })}>
              Mark done now
            </Button>
          )}
          <Button size="small" disabled={busy} onClick={startEdit}>
            {n.state === "done" ? "Edit when / who" : "Done earlier…"}
          </Button>
          {n.state === "done" && (
            <Button size="small" disabled={busy} onClick={() => onPut(n.code, { done_at: null })}>
              Not done
            </Button>
          )}
          {n.state !== "not_needed" ? (
            <Button size="small" disabled={busy} onClick={startNotNeeded}>Not needed…</Button>
          ) : (
            <Button size="small" disabled={busy}
                    onClick={() => onPut(n.code, { not_needed_reason: null })}>
              It is needed
            </Button>
          )}
          <Button size="small" startIcon={<UploadFileIcon />} disabled={busy}
                  onClick={() => fileRef.current?.click()}>
            {n.document ? "Replace copy" : "Attach copy"}
          </Button>
          {n.document && (
            <Button size="small" disabled={busy} color="error"
                    onClick={() => window.confirm("Remove the attached copy?")
                      && onPut(n.code, { document_id: null })}>
              Remove copy
            </Button>
          )}
          {n.basis === "added" && (
            <Button size="small" disabled={busy} color="error"
                    onClick={() => onPut(n.code, { added: false })}>
              Take off
            </Button>
          )}
          <input ref={fileRef} type="file" hidden
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(n.code, f); }} />
        </Box>
      )}
    </Box>
  );
}

function Notifications({ report, canEdit, busy, onPut, onUpload }) {
  const [catalogue, setCatalogue] = useState([]);
  // Only for adding one by hand, so only fetched for an editor — a read-only
  // reader (the event's Clinical Lead, the report's raiser) isn't served it.
  useEffect(() => {
    if (!canEdit) return;
    axios.get(`${API}/notification-catalogue`).then(res => setCatalogue(res.data)).catch(() => {});
  }, [canEdit]);
  const shown = new Set([...report.notifications, ...report.suggested_notifications].map(n => n.code));
  const others = catalogue.filter(c => !shown.has(c.code));
  return (
    <Stack spacing={1.5}>
      {report.notifications.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {report.harm ? "Nothing needs notifying for this type and degree of harm."
            : "Nothing needs notifying for this type. Set the degree of harm — it may change that."}
        </Typography>
      )}
      {report.notifications.map(n => (
        <NotificationCard key={n.code} n={n} canEdit={canEdit} busy={busy}
                          onPut={onPut} onUpload={onUpload} />
      ))}
      {canEdit && report.suggested_notifications.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Might also apply — depends on what happened:
          </Typography>
          <Stack spacing={0.75}>
            {report.suggested_notifications.map(s => (
              <Box key={s.code} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {s.what} <Box component="span" sx={{ color: "text.secondary" }}>({s.who} → {s.to})</Box>
                </Typography>
                <Button size="small" disabled={busy} onClick={() => onPut(s.code, { added: true })}>
                  Add
                </Button>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      {canEdit && others.length > 0 && (
        <TextField select size="small" label="Add another notification" value=""
                   sx={{ maxWidth: 480 }} disabled={busy}
                   onChange={(e) => onPut(e.target.value, { added: true })}>
          {others.map(c => (
            <MenuItem key={c.code} value={c.code} sx={{ whiteSpace: "normal" }}>
              {c.to}: {c.what}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}

// --- The dialog ---------------------------------------------------------------

export default function ClinicalReportDialog({ reportId, onClose, onSaved, onDeleted }) {
  const fullScreen = useFullScreenDialog();
  const { can } = useMe();
  const me = useCurrentUser();
  const canEdit = can("ClinicalReports.edit");
  const [id, setId] = useState(null);          // the saved report's id, once there is one
  const [report, setReport] = useState(null);  // as last served
  const [form, setForm] = useState(emptyReportForm);
  const [events, setEvents] = useState([]);
  const [entrants, setEntrants] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [note, setNote] = useState("");
  const { busy, run } = useWriteLock();

  const take = useCallback((data) => {
    setReport(data);
    setForm(reportToForm(data));
    setId(data.id);
  }, []);

  useEffect(() => {
    setReport(null); setNote(""); setForm(emptyReportForm()); setId(null);
    if (reportId == null || reportId === "new") return;
    axios.get(`${API}/${reportId}`).then(res => take(res.data)).catch(() => onClose());
    // Not on onClose: callers pass an inline function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, take]);

  useEffect(() => {
    if (reportId == null || !canEdit) return;
    axios.get(`${API_BASE_URL}/events/`).then(res => setEvents(res.data)).catch(() => {});
  }, [reportId, canEdit]);

  const eventId = form.event_id === "" ? null : Number(form.event_id);
  useEffect(() => {
    setEntrants([]); setIncidents([]);
    if (eventId == null || !canEdit) return;
    axios.get(`${API}/participants`, { params: { event_id: eventId } })
      .then(res => setEntrants(res.data)).catch(() => {});
    axios.get(`${API}/incidents`, { params: { event_id: eventId } })
      .then(res => setIncidents(res.data)).catch(() => {});
  }, [eventId, canEdit]);

  const savedForm = useMemo(() => (report ? reportToForm(report) : emptyReportForm()), [report]);
  const dirty = JSON.stringify(reportBody(form)) !== JSON.stringify(reportBody(savedForm));
  const problems = reportProblems(form);

  if (reportId == null) return null;
  const isNew = id == null;
  const loading = !isNew ? report == null : false;
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const setValue = (key) => (v) => setForm(f => ({ ...f, [key]: v }));

  const save = () => run(() => (isNew ? axios.post(`${API}/`, reportBody(form))
    : axios.put(`${API}/${id}`, reportBody(form))).then(res => {
    take(res.data);
    onSaved?.(res.data);
    notify(isNew ? `${res.data.reference} created.` : "Saved.", "success");
  }));

  // Writes that save on their own (notifications, candour stamp) return the whole
  // record. Take it, but keep any unsaved edits to the form.
  const takeKeepingEdits = (data) => {
    const edits = dirty ? form : null;
    setReport(data);
    onSaved?.(data);
    if (edits) {
      const served = reportToForm(data);
      setForm({ ...edits, candour_at: served.candour_at, candour_by: served.candour_by });
    } else {
      setForm(reportToForm(data));
    }
  };

  const putNotification = (code, body) => run(() =>
    axios.put(`${API}/${id}/notifications/${code}`, body).then(res => takeKeepingEdits(res.data)));
  const uploadNotification = (code, file) => {
    const fd = new FormData();
    fd.append("file", file);
    run(() => axios.post(`${API}/${id}/notifications/${code}/document`, fd)
      .then(res => { takeKeepingEdits(res.data); notify("Copy attached.", "success"); }));
  };

  const stampCandour = () => {
    if (isNew) {
      setForm(f => ({ ...f, candour_at: toLocalInput(new Date().toISOString()), candour_by: me.name || f.candour_by }));
      return;
    }
    run(() => axios.post(`${API}/${id}/candour`).then(res => takeKeepingEdits(res.data)));
  };

  const addNote = () => run(() => axios.post(`${API}/${id}/notes`, { details: note }).then(res => {
    const next = { ...report, notes: [...report.notes, res.data] };
    setReport(next); onSaved?.(next); setNote("");
  }));
  const deleteNote = (n) => {
    if (!window.confirm("Delete this note?")) return;
    run(() => axios.delete(`${API}/notes/${n.id}`).then(() => {
      const next = { ...report, notes: report.notes.filter(x => x.id !== n.id) };
      setReport(next); onSaved?.(next);
    }));
  };
  const deleteReport = () => {
    if (!window.confirm(`Delete ${report.reference} permanently? Use this for mistakes and tests only — `
      + "a real report should be closed instead.")) return;
    run(() => axios.delete(`${API}/${id}`).then(() => { onDeleted?.(id); onClose(); }));
  };
  const close = () => {
    if (dirty && canEdit && !window.confirm("Close without saving your changes?")) return;
    onClose();
  };

  const changeEvent = (e) => setForm(f => ({
    ...f, event_id: e.target.value,
    // An entrant and an incident belong to their event.
    ...(String(e.target.value) !== String(f.event_id) ? { participant_id: null, incident_id: null } : {}),
  }));
  const chosenEntrant = entrants.find(p => p.id === form.participant_id) || null;
  const chosenIncident = incidents.find(i => i.id === form.incident_id) || null;
  const canOpenIncidents = eventId != null && can("Incidents.view", { eventId });
  // The panels read the saved record: say so while the form says something else.
  const savedEvent = report?.event_id ?? null;
  const affectedKey = report && savedEvent != null && (report.participant_id || report.affected_race_number)
    ? `${report.participant_id}|${report.affected_race_number}|${savedEvent}` : null;
  const personUnsaved = report && (form.participant_id !== savedForm.participant_id
    || form.affected_race_number !== savedForm.affected_race_number
    || String(form.event_id) !== String(savedForm.event_id));
  const incidentPatientDiffers = chosenIncident && chosenIncident.patient_name
    && (chosenIncident.patient_name !== form.affected_name
      || (chosenIncident.patient_participant_id ?? null) !== form.participant_id);
  const ro = !canEdit;

  return (
    <Dialog open onClose={busy ? undefined : close} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>
        {isNew ? "New clinical report"
          : report ? `${report.reference} — ${report.report_type}` : "Clinical report"}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <CircularProgress /> : (
          <Stack spacing={3}>
            {report && (
              <Typography variant="caption" color="text.secondary">
                Raised {fmtDateTime(report.created_at)}{report.created_by ? ` by ${report.created_by}` : ""}
                {report.updated_at && report.updated_at !== report.created_at
                  ? ` · last changed ${fmtDateTime(report.updated_at)}` : ""}
              </Typography>
            )}
            <Section title="Report">
              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                {ro ? (
                  <TextField label="Event" value={report?.event_label || "Not at an event"} disabled />
                ) : (
                  <TextField select label="Event" value={form.event_id} onChange={changeEvent}>
                    <MenuItem value=""><em>Not at an event</em></MenuItem>
                    {events.map(ev => <MenuItem key={ev.id} value={ev.id}>{ev.label_long || ev.name}</MenuItem>)}
                    {/* The stored event, should the list not have arrived or not include it. */}
                    {report?.event_id != null && !events.some(ev => ev.id === report.event_id) && (
                      <MenuItem value={report.event_id}>{report.event_label}</MenuItem>
                    )}
                  </TextField>
                )}
                <TextField type="datetime-local" label="When it happened" value={form.occurred_at}
                           onChange={set("occurred_at")} disabled={ro}
                           slotProps={{ inputLabel: { shrink: true } }}
                           helperText={isNew ? "Leave blank for now. Notification deadlines run from this." :
                             "Notification deadlines run from this."} />
              </Box>
              <HintedSelect label="Type" options={CLINICAL_REPORT_TYPES} value={form.report_type}
                            onChange={setValue("report_type")} allowBlank={false} disabled={ro} required />
              {form.report_type === "Other" && (
                <TextField label="Type of report" value={form.type_other} onChange={set("type_other")}
                           disabled={ro} required error={!form.type_other.trim()} />
              )}
              <TextField label="Details" value={form.details} onChange={set("details")} disabled={ro}
                         multiline minRows={4} required
                         helperText="What happened, and what was done about it at the time." />
              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                <HintedSelect label="Degree of harm" options={CLINICAL_HARM_LEVELS} value={form.harm}
                              onChange={setValue("harm")} disabled={ro}
                              helperText="Not yet assessed" />
                <TextField select label="Status" value={form.status} onChange={set("status")} disabled={ro}
                           helperText="Following Up once someone is acting on it; Closed when that's finished.">
                  {CLINICAL_REPORT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Box>
            </Section>

            <Divider />
            <Section title="Person affected">
              {!ro && eventId != null && (
                <Autocomplete
                  options={entrants}
                  value={chosenEntrant}
                  onChange={(_, p) => setForm(f => (p ? { ...f, ...affectedFromParticipant(p) }
                    : { ...f, participant_id: null }))}
                  getOptionLabel={participantLabel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  filterOptions={(opts, { inputValue }) => {
                    const m = participantMatches(inputValue, opts);
                    return inputValue.trim() ? [...m.exact, ...m.partial] : opts;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Entrant on the entry list"
                               helperText={form.participant_id ? "Linked — their details are copied below"
                                 : "Search by name or race number, or fill in the details below for anyone else"} />
                  )}
                />
              )}
              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" } }}>
                <TextField size="small" label="Name" value={form.affected_name} onChange={set("affected_name")} disabled={ro} />
                <TextField size="small" label="Race number" value={form.affected_race_number}
                           onChange={set("affected_race_number")} disabled={ro} />
                <TextField size="small" label="Age" type="number" value={form.affected_age}
                           onChange={set("affected_age")} disabled={ro} />
                <TextField size="small" label="Race" value={form.affected_race} onChange={set("affected_race")} disabled={ro} />
                <TextField size="small" label="Phone" value={form.affected_phone} onChange={set("affected_phone")} disabled={ro} />
                <TextField size="small" label="Email" value={form.affected_email} onChange={set("affected_email")} disabled={ro} />
              </Box>
              {personUnsaved && (
                <Typography variant="caption" color="text.secondary">
                  Save to see this person's entry and incidents.
                </Typography>
              )}
              {!personUnsaved && id != null && (
                <AffectedPanel reportId={id} savedKey={affectedKey} canOpen={canOpenIncidents} />
              )}
            </Section>

            <Divider />
            <Section title="Related incident">
              {eventId == null ? (
                <Typography variant="body2" color="text.secondary">
                  Choose the event to link one of its incidents.
                </Typography>
              ) : !ro && (
                <Autocomplete
                  options={incidents}
                  value={chosenIncident}
                  onChange={(_, i) => setForm(f => ({ ...f, incident_id: i ? i.id : null }))}
                  getOptionLabel={incidentLabel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Incident this is about, or that led to it" />
                  )}
                />
              )}
              {!ro && incidentPatientDiffers && (
                <Alert severity="info" action={
                  <Button size="small" onClick={() => setForm(f => ({ ...f, ...affectedFromIncident(chosenIncident) }))}>
                    Use them
                  </Button>}>
                  The incident's patient is <strong>{chosenIncident.patient_name}</strong>
                  {chosenIncident.patient_race_number ? ` (#${chosenIncident.patient_race_number})` : ""}.
                  Make them the person affected?
                </Alert>
              )}
              {id != null && report?.incident_id != null && report.incident_id === form.incident_id && (
                <RelatedIncident reportId={id} incidentId={report.incident_id} canOpen={canOpenIncidents} />
              )}
            </Section>

            <Divider />
            <Section title="Duty of candour at the time"
                     action={!ro && !form.candour_at && (
                       <Button size="small" variant="outlined" disabled={busy} onClick={stampCandour}>
                         I've told them — record now
                       </Button>
                     )}>
              <Typography variant="body2" color="text.secondary">
                When the patient or person affected was told what happened, and by whom.
              </Typography>
              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                <TextField size="small" type="datetime-local" label="Told at" value={form.candour_at}
                           onChange={set("candour_at")} disabled={ro}
                           slotProps={{ inputLabel: { shrink: true } }} />
                <TextField size="small" label="Told by" value={form.candour_by} onChange={set("candour_by")}
                           disabled={ro || !form.candour_at}
                           error={!!form.candour_at && !form.candour_by.trim()} />
              </Box>
            </Section>

            <Divider />
            <Section title="Notifications">
              {isNew ? (
                <Typography variant="body2" color="text.secondary">
                  Save the report to see who must be notified, and by when.
                </Typography>
              ) : (
                <>
                  {(form.report_type !== savedForm.report_type || form.harm !== savedForm.harm) && (
                    <Alert severity="info">Save to update these for the new type or degree of harm.</Alert>
                  )}
                  <Notifications report={report} canEdit={canEdit} busy={busy}
                                 onPut={putNotification} onUpload={uploadNotification} />
                </>
              )}
            </Section>

            {!isNew && (
              <>
                <Divider />
                <Section title={`Notes (${report.notes.length})`}>
                  {report.notes.map(n => (
                    <Box key={n.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1,
                                          display: "flex", gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{n.details}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {n.added_by || "—"} — {fmtDateTime(n.added_at)}
                        </Typography>
                      </Box>
                      {canEdit && (
                        <Tooltip title="Delete note">
                          <IconButton size="small" onClick={() => deleteNote(n)} disabled={busy}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                  {canEdit && (
                    <Stack spacing={1}>
                      <TextField label="Add a note" fullWidth multiline minRows={2} value={note}
                                 onChange={(e) => setNote(e.target.value)}
                                 helperText="How the follow-up is going, what was learned…" />
                      <Box>
                        <Button variant="outlined" disabled={!note.trim() || busy} onClick={addNote}>
                          Add note
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Section>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {report && can("ClinicalReports.delete") && (
          <Button color="error" onClick={deleteReport} disabled={busy} sx={{ mr: "auto" }}>
            Delete
          </Button>
        )}
        {canEdit && problems.length > 0 && dirty && (
          <Typography variant="caption" color="warning.main">{problems[0]}</Typography>
        )}
        <Button onClick={close} disabled={busy}>{dirty && canEdit ? "Cancel" : "Close"}</Button>
        {canEdit && (
          <Button variant="contained" onClick={save} disabled={busy || !dirty || problems.length > 0}>
            {isNew ? "Create" : "Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
